// src/pages/Login.js
"use client";
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Tabs,
  Tab,
  Stack,
  Alert,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Visibility from "@mui/icons-material/Visibility";
import { useAuth } from "../auth/AuthContext";
import ThemeToggleButton from "../components/ThemeToggleButton";
import { useNavigate, useLocation, Link as RouterLink } from "react-router-dom";

export default function Login() {
  // From AuthContext
  const { signIn, signUp, confirmSignUp, resendSignUpCode } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const isDark = theme.palette.mode === "dark";
  const logoSrc =
    theme.palette.mode === "light"
      ? "/brand/SemiQolon-Black.png"
      : "/brand/SemiQolon.png";

  // Tabs: 0=Sign in, 1=Sign up, 2=Confirm
  const [tab, setTab] = useState(0);

  // Form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [code, setCode] = useState("");

  // UX state
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [busySignIn, setBusySignIn] = useState(false);
  const [busySignUp, setBusySignUp] = useState(false);
  const [busyConfirm, setBusyConfirm] = useState(false);
  const [busyResend, setBusyResend] = useState(false);

  // Resend cooldown
  const [resendSeconds, setResendSeconds] = useState(0);
  useEffect(() => {
    if (resendSeconds <= 0) return;
    const t = setInterval(() => setResendSeconds((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendSeconds]);

  const bgTop = alpha(theme.palette.background.default, isDark ? 0.6 : 0.95);
  const bgBottom = theme.palette.background.paper;

  // Prefer: state.from (ProtectedRoute) → ?next=/path → /studio
  const safeNext = () => {
    const fromState = location?.state?.from;
    if (typeof fromState === "string" && fromState.startsWith("/")) return fromState;
    const params = new URLSearchParams(location.search);
    const raw = params.get("next") || "";
    if (raw && raw.startsWith("/")) return raw;
    return "/studio";
  };

  const goHome = () => navigate(safeNext(), { replace: true });

  const clearMessages = () => {
    setErr("");
    setInfo("");
  };

  const switchTab = (next) => {
    setTab(next);
    clearMessages();
  };

  // Actions
  const trySignIn = async () => {
    clearMessages();
    setBusySignIn(true);
    try {
      await signIn({ email: email.trim(), password });
      goHome();
    } catch (e) {
      const msg = e?.message || "Sign in failed";
      const codeName = e?.code || e?.name || "";
      const unconfirmed =
        /not.*confirm|not.*verified/i.test(msg) || /UserNotConfirmedException/i.test(codeName);
      if (unconfirmed) {
        setTab(2);
        setErr("Please verify your email before signing in.");
        setInfo("Enter your verification code below, or resend a new code.");
      } else {
        setErr(msg);
      }
    } finally {
      setBusySignIn(false);
    }
  };

  const trySignUp = async () => {
    clearMessages();
    setBusySignUp(true);
    try {
      await signUp({ email: email.trim(), password });
      setTab(2);
      setInfo("We sent a verification code to your email. Enter it below to confirm your account.");
      setResendSeconds(30);
    } catch (e) {
      setErr(e?.message || "Sign up failed");
    } finally {
      setBusySignUp(false);
    }
  };

  const tryConfirm = async () => {
    clearMessages();
    setBusyConfirm(true);
    try {
      await confirmSignUp({ email: email.trim(), code: code.trim() });
      setTab(0);
      setInfo("Your email has been verified. You can now sign in.");
    } catch (e) {
      setErr(e?.message || "Confirmation failed");
    } finally {
      setBusyConfirm(false);
    }
  };

  const resendCode = async () => {
    clearMessages();
    if (!email.trim()) {
      setErr("Enter your email above first, then click Resend.");
      return;
    }
    if (!resendSignUpCode) {
      setErr("Resend is not available right now. Please contact support.");
      return;
    }
    setBusyResend(true);
    try {
      await resendSignUpCode({ email: email.trim() });
      setInfo("Verification code sent. Check your inbox (and spam).");
      setResendSeconds(30);
    } catch (e) {
      setErr(e?.message || "Failed to resend verification code");
    } finally {
      setBusyResend(false);
    }
  };

  // Submit handler — fixes the Chrome warning by using a real form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (tab === 0) {
      if (!email || !password || busySignIn) return;
      await trySignIn();
    } else if (tab === 1) {
      if (!email || !password || busySignUp) return;
      await trySignUp();
    } else if (tab === 2) {
      if (!email || !code || busyConfirm) return;
      await tryConfirm();
    }
  };

  // Helpful computed disables
  const canSignIn = useMemo(() => email && password && !busySignIn, [email, password, busySignIn]);
  const canSignUp = useMemo(() => email && password && !busySignUp, [email, password, busySignUp]);
  const canConfirm = useMemo(() => email && code && !busyConfirm, [email, code, busyConfirm]);
  const canResend = useMemo(
    () => !!email && !busyResend && resendSeconds === 0,
    [email, busyResend, resendSeconds]
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        p: 2,
        background: `linear-gradient(180deg, ${bgTop} 0%, ${bgBottom} 100%)`,
      }}
    >
      <Box component={RouterLink} to="/" sx={{ position: "fixed", top: 12, left: 12 }}>
        <Box
          component="img"
          src={logoSrc}
          alt="SemiQolon Studio"
          sx={{ height: 32, width: "auto", display: "block" }}
          loading="eager"
        />
      </Box>

      <Box sx={{ position: "fixed", top: 12, right: 12 }}>
        <ThemeToggleButton />
      </Box>

      <Paper
        sx={{
          p: 3,
          width: 420,
          maxWidth: "96vw",
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        {/* Title row */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>
            Welcome
          </Typography>
          <Box
            component="img"
            src={logoSrc}
            alt="SemiQolon Studio"
            sx={{ height: 28, width: "auto", display: "block" }}
            loading="eager"
          />
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Sign in to manage your assistants.
        </Typography>

        <Tabs value={tab} onChange={(_e, v) => switchTab(v)} variant="fullWidth" sx={{ mb: 2 }}>
          <Tab label="Sign in" sx={{ textTransform: "none", fontWeight: 700 }} />
          <Tab label="Create account" sx={{ textTransform: "none", fontWeight: 700 }} />
          <Tab label="Verify account" sx={{ textTransform: "none", fontWeight: 700 }} />
        </Tabs>

        {!!err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        {!!info && <Alert severity="info" sx={{ mb: 2 }}>{info}</Alert>}

        {/* ✅ Wrap inputs in a real form to satisfy Chrome */}
        <form onSubmit={handleSubmit} autoComplete="on" noValidate>
          {/* Email field (shared) */}
          <TextField
            label="Email"
            type="email"
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={{ mb: 1.5 }}
            autoComplete="email username"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon />
                </InputAdornment>
              ),
            }}
            required
          />

          {/* Password (Sign in + Sign up) */}
          {tab !== 2 && (
            <TextField
              label="Password"
              type={showPw ? "text" : "password"}
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{ mb: 1.5 }}
              autoComplete={tab === 1 ? "new-password" : "current-password"}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPw ? "Hide password" : "Show password"}
                      onClick={() => setShowPw((s) => !s)}
                      edge="end"
                    >
                      {showPw ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              required
            />
          )}

          {/* Confirm tab: code input + resend */}
          {tab === 2 && (
            <>
              <TextField
                label="Verification code"
                fullWidth
                value={code}
                onChange={(e) => setCode(e.target.value)}
                sx={{ mb: 1.5 }}
                autoComplete="one-time-code"
                required
              />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 1.5 }}>
                <Button
                  variant="outlined"
                  type="button"
                  onClick={resendCode}
                  disabled={!canResend}
                >
                  {busyResend
                    ? "Sending…"
                    : resendSeconds > 0
                    ? `Resend in ${resendSeconds}s`
                    : "Resend verification code"}
                </Button>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  Didn’t receive it? Check spam or try again.
                </Typography>
              </Stack>
            </>
          )}

          {/* Primary action row */}
          <Stack direction="row" spacing={1}>
            {tab === 0 && (
              <Button
                variant="contained"
                fullWidth
                type="submit"
                disabled={!canSignIn}
              >
                {busySignIn ? "Signing in…" : "Sign in"}
              </Button>
            )}
            {tab === 1 && (
              <Button
                variant="contained"
                fullWidth
                type="submit"
                disabled={!canSignUp}
              >
                {busySignUp ? "Creating…" : "Create account"}
              </Button>
            )}
            {tab === 2 && (
              <Button
                variant="contained"
                fullWidth
                type="submit"
                disabled={!canConfirm}
              >
                {busyConfirm ? "Confirming…" : "Confirm"}
              </Button>
            )}
          </Stack>

          {/* Quick back link when not on Sign in */}
          {tab !== 0 && (
            <Button
              variant="text"
              type="button"
              onClick={() => switchTab(0)}
              disabled={busySignIn || busySignUp || busyConfirm}
              sx={{ mt: 1.5 }}
            >
              Back to sign in
            </Button>
          )}
        </form>
      </Paper>
    </Box>
  );
}
